import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import {
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Visibility as EyeIcon,
} from '@mui/icons-material';
import { NLVisualAssertionResult as NLVisualAssertionResultType } from '../types';

interface NLVisualAssertionResultProps {
  result: NLVisualAssertionResultType;
}

const NLVisualAssertionResult: React.FC<NLVisualAssertionResultProps> = ({ result }) => {
  const confidencePct = Math.round(result.confidence * 100);
  const confidenceColor: 'success' | 'warning' | 'error' =
    result.confidence >= 0.9 ? 'success' : result.confidence >= 0.7 ? 'warning' : 'error';

  return (
    <Box sx={{ mb: 1.5 }}>
      {/* Pass/Fail banner */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 1,
          bgcolor: result.passed ? 'success.light' : 'error.light',
          border: '1px solid',
          borderColor: result.passed ? 'success.main' : 'error.main',
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {result.passed ? (
          <PassIcon color="success" />
        ) : (
          <FailIcon color="error" />
        )}
        <Typography variant="subtitle2" fontWeight={700}>
          Visual Assertion {result.passed ? 'PASSED' : 'FAILED'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Chip
          size="small"
          label={`${confidencePct}% confidence`}
          color={confidenceColor}
          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
        />
      </Paper>

      {/* Assertion text */}
      <Box
        sx={{
          borderLeft: '3px solid',
          borderColor: 'primary.main',
          pl: 2,
          mb: 1.5,
          py: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
          <EyeIcon fontSize="small" color="action" />
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Assertion
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          "{result.assertion_text}"
        </Typography>
      </Box>

      {/* Selector if present */}
      {result.selector && (
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'monospace',
            bgcolor: 'grey.100',
            px: 1,
            py: 0.25,
            borderRadius: 0.5,
            display: 'inline-block',
            mb: 1,
          }}
        >
          {result.selector}
        </Typography>
      )}

      {/* Screenshot */}
      {result.screenshot_b64 && (
        <Box sx={{ mb: 1.5 }}>
          <img
            src={`data:image/png;base64,${result.screenshot_b64}`}
            alt="Visual assertion screenshot"
            loading="lazy"
            style={{
              maxWidth: '100%',
              maxHeight: 300,
              borderRadius: 8,
              border: '1px solid #e0e0e0',
              display: 'block',
            }}
          />
        </Box>
      )}

      {/* Reasoning */}
      <Typography variant="body2" color="text.secondary">
        {result.reasoning}
      </Typography>
    </Box>
  );
};

export default NLVisualAssertionResult;
